import type { AuditEventView } from "@kopi/contracts";
import { AUDIT_SORTS, DEFAULT_AUDIT_SORT } from "@kopi/contracts/client";
import { AUDIT_ACTIONS, type AuditAction } from "@kopi/contracts/codes";
import { Combobox, type ComboboxPage } from "@kopi/ui/components/combobox";
import { DataTable, type DataTableColumn } from "@kopi/ui/components/data-table";
import { DatePicker } from "@kopi/ui/components/date-picker";
import { Label } from "@kopi/ui/components/label";
import { useInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useId, useMemo, useState } from "react";
import { Page, PageHeader } from "@/components/page-header";
import { audit } from "@/i18n/messages/audit";
import { useI18n, useMessages } from "@/i18n/provider";
import type { TranslateOf } from "@/i18n/translate";
import { type AuditFilters, auditEventsQuery } from "@/lib/queries";
import { sortSearch, urlSorting } from "@/lib/sorting";

/** The column sort lives in the URL (R1-09d), so it survives a reload. */
const validateSearch = sortSearch(AUDIT_SORTS);

export const Route = createFileRoute("/_authed/audit")({
  staticData: { section: "workspace", crumb: "nav.audit" },
  validateSearch,
  component: AuditPage,
});

type T = TranslateOf<typeof audit.id>;

const actionLabel = (t: T, action: AuditAction) => t(`audit.action.${action}`);

/** Events per page: a table page on desktop, one infinite-scroll step on phones. */
const AUDIT_PAGE = 10;
/** Actions per step of the action picker. */
const ACTION_PAGE = 30;

interface ActionOption {
  value: AuditAction;
  label: string;
}

/**
 * The action picker's pages (CODING_STANDARDS §5: a searchable Combobox). The actions are a fixed code
 * list translated on the device, so search matches the label in the interface language (or the code),
 * and the cursor is the offset into the matches.
 */
function actionPages(t: T) {
  const options: ActionOption[] = AUDIT_ACTIONS.map((value) => ({ value, label: actionLabel(t, value) }));
  return async (q: string, cursor: string | undefined): Promise<ComboboxPage<ActionOption>> => {
    const needle = q.toLocaleLowerCase();
    const matches = needle
      ? options.filter((o) => o.label.toLocaleLowerCase().includes(needle) || o.value.includes(needle))
      : options;
    const start = cursor ? Number(cursor) : 0;
    const end = start + ACTION_PAGE;
    return { items: matches.slice(start, end), nextCursor: end < matches.length ? String(end) : null };
  };
}

/** When an event happened, in the interface language. */
function When(props: { iso: string }) {
  const { format } = useI18n();
  return (
    <time dateTime={props.iso} className="text-muted-foreground">
      {format.dateTime(props.iso)}
    </time>
  );
}

function describeState(state: Record<string, unknown> | null): string | undefined {
  if (!state) return undefined;
  return Object.entries(state)
    .map(([key, value]) => `${key}: ${typeof value === "object" ? JSON.stringify(value) : String(value)}`)
    .join(", ");
}

const columnsFor = (t: T): DataTableColumn<AuditEventView>[] => [
  {
    id: "action",
    header: t("audit.action"),
    meta: { card: "title" },
    cell: ({ row }) => <span className="font-medium">{actionLabel(t, row.original.action)}</span>,
  },
  {
    id: "occurredAt",
    header: t("audit.when"),
    enableSorting: true,
    meta: { card: "subtitle", className: "md:w-44" },
    cell: ({ row }) => <When iso={row.original.occurredAt} />,
  },
  {
    id: "actor",
    header: t("audit.by"),
    cell: ({ row }) => {
      const { actor, subject } = row.original;
      return (
        <span>
          {actor.displayName ?? t(actor.userId ? "audit.unknownAccount" : "audit.noSession")}
          {subject.displayName && (
            <span className="text-muted-foreground"> · {t("audit.for", { name: subject.displayName })}</span>
          )}
        </span>
      );
    },
  },
  {
    id: "detail",
    header: t("audit.detail"),
    meta: { className: "md:w-[40%]" },
    cell: ({ row }) => <AuditDetail event={row.original} />,
  },
];

function AuditDetail({ event }: { event: AuditEventView }) {
  const { t } = useMessages(audit);
  const before = describeState(event.before);
  const after = describeState(event.after);
  const ip = typeof event.metadata.ip === "string" ? event.metadata.ip : undefined;
  const trace = [
    ip ? t("audit.network", { ip }) : undefined,
    event.requestId ? t("audit.request", { id: event.requestId }) : undefined,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <div className="flex flex-col gap-1 text-left text-sm">
      {event.reason && <p>{t("audit.reason", { reason: event.reason })}</p>}
      {(before || after) && (
        <dl className="grid gap-0.5 rounded-md bg-muted p-2 font-mono text-xs">
          {before && (
            <div>
              <dt className="inline">{t("audit.before")} </dt>
              <dd className="inline break-all">{before}</dd>
            </div>
          )}
          {after && (
            <div>
              <dt className="inline">{t("audit.after")} </dt>
              <dd className="inline break-all">{after}</dd>
            </div>
          )}
        </dl>
      )}
      {trace && <p className="break-all text-muted-foreground text-xs">{trace}</p>}
    </div>
  );
}

function AuditPage() {
  const { t, locale } = useMessages(audit);
  const columns = useMemo(() => columnsFor(t), [t]);
  const fetchActions = useMemo(() => actionPages(t), [t]);
  const [filters, setFilters] = useState<AuditFilters>({ action: "", from: "", to: "" });
  const { sort } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const sorting = urlSorting(AUDIT_SORTS, DEFAULT_AUDIT_SORT, sort, (next) => {
    void navigate({ search: next ? { sort: next } : {}, replace: true });
  });
  const fromId = useId();
  const fromLabelId = useId();
  const toId = useId();
  const toLabelId = useId();
  const events = useInfiniteQuery(auditEventsQuery({ ...filters, sort }, AUDIT_PAGE));

  return (
    <Page>
      <PageHeader title={t("audit.title")} subtitle={t("audit.subtitle")} />
      <DataTable
        label={t("audit.title")}
        columns={columns}
        query={events}
        getRowId={(event) => event.id}
        estimateRowHeight={96}
        paging="pages"
        {...sorting}
        filters={
          <>
            <Combobox
              aria-label={t("audit.action")}
              className="w-full md:w-64"
              fetchPage={fetchActions}
              getValue={(option) => option.value}
              getLabel={(option) => option.label}
              value={filters.action || null}
              selectedLabel={filters.action ? actionLabel(t, filters.action) : undefined}
              onChange={(_, picked) => setFilters({ ...filters, action: picked?.value ?? "" })}
              placeholder={t("audit.allActions")}
              searchPlaceholder={t("audit.searchActions")}
              clearable
              clearLabel={t("audit.allActions")}
            />
            {/* Fixed, equal widths: picking or clearing a date never moves the other field. Stacked on a
                phone, where half a row cannot show a whole date beside its clear button. */}
            <div className="grid w-full gap-2 sm:grid-cols-2 md:flex md:w-auto">
              <Label id={fromLabelId} htmlFor={fromId} className="sr-only">
                {t("audit.from")}
              </Label>
              <DatePicker
                locale={locale}
                id={fromId}
                labelId={fromLabelId}
                value={filters.from}
                onChange={(from) => setFilters({ ...filters, from })}
                placeholder={t("audit.from")}
                className="min-w-0 md:w-48"
              />
              <Label id={toLabelId} htmlFor={toId} className="sr-only">
                {t("audit.to")}
              </Label>
              <DatePicker
                locale={locale}
                id={toId}
                labelId={toLabelId}
                value={filters.to}
                onChange={(to) => setFilters({ ...filters, to })}
                placeholder={t("audit.to")}
                className="min-w-0 md:w-48"
              />
            </div>
          </>
        }
        empty={{
          title: t("audit.emptyTitle"),
          description: t("audit.emptyHint"),
        }}
      />
    </Page>
  );
}
